import React from "react";

/**
 * Tabs Component
 *
 * A reusable tab navigation component with dark mode support.
 *
 * @param {Array} tabs - Array of tab objects with { id, label, count? }
 * @param {string} activeTab - Currently active tab id
 * @param {Function} onTabChange - Callback when tab is clicked, receives tab id
 *
 * @example
 * <Tabs
 *   tabs={[
 *     { id: 'all', label: 'All', count: 24 },
 *     { id: 'pending', label: 'Pending', count: 6 }
 *   ]}
 *   activeTab="all"
 *   onTabChange={(id) => setActiveTab(id)}
 * />
 */
const Tabs = ({ tabs, activeTab, onTabChange }) => {
  return (
    <div className="border-b border-gray-200 dark:border-gray-700">
      <nav className="flex space-x-1 overflow-x-auto no-scrollbar" aria-label="Tabs">
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id;

          return (
            <button
              key={tab.id}
              onClick={() => onTabChange(tab.id)}
              className={`
                px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors
                border-b-2 -mb-px
                ${
                  isActive
                    ? "border-green-600 text-green-700 dark:text-green-400 dark:border-green-500"
                    : "border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-gray-600"
                }
              `}
            >
              {tab.label}
              {tab.count !== undefined && (
                <span
                  className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                    isActive
                      ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                      : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400"
                  }`}
                >
                  {tab.count}
                </span>
              )}
            </button>
          );
        })}
      </nav>
    </div>
  );
};

export default Tabs;
